import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import * as Commons from "oc-commons/Commons";
import { v3 } from "oc-threejs/short3";
import { getMMR } from "../RichMetadata";
import { Pfosten3d } from "../arrangement3d/Pfosten3d";
import { Anker } from "./Anker";
import _ from "lodash";
import { heightBoxWorldPhysical, JSON_DECIMALS } from "../commons/Utils";
import {
  getBoxUnclipped,
  getMPfostenBreite,
  getPfostenMetaParameter,
  getPfostenMetaParameterFromPfosten,
  getUMProfilModel,
  getVorläufigeElementeAnEcke,
  isLongAnschlag,
  PfostenWinkel,
  updateTeilModel,
} from "./pfostenFunctions";
import * as Maths from "oc-commons/Maths";
import * as Maths3 from "oc-threejs/Maths3";
import { ModelGhost } from "oc-jaco3d/arrangement/ModelGhost";
import { getTypeNameOfPart } from "./arrangementFunctions";
import { findPathIntersectionCached } from "oc-app/metadata/GraphMetadata";
import { findMatchingRotation, MatchingRotation, normalizeAngle } from "../commons/AngleIntervals";
import { pfostenbreiteCirca } from "./constants";
import { MAuswahlwert, MObjekt, MPfosten, MUMProfil } from "../../metamodell";
import { PAuswahlwert, PPfosten } from "../../metamodell_paths";
import { MyModel } from "./MyModel";
import { BestelllistenEintrag, StücklistenIterator } from "./Stückliste";
import { Ecke } from "./Ecke";
import { ZElement } from "./ZElement";
import { Part } from "./Part";
import { Aufbau } from "./Aufbau";
import { BoxRuler } from "../arrangement3d/BoxRuler";
import { Arrangement, ghostPool } from "./Arrangement";
import { Box3 } from "three";
import { Segment } from "./Segment";
import { Fundament } from "./Fundament";
import { v2 } from "oc-threejs/short3";

/**
 * Findet die Pfostenfarbe-Auswahlwert, die zum Rahmen-Auswahlwert eines Schiebetors passt.
 * Primär über die Farbmapping-Tabelle, sekundär über den Namen (Anthrazit, Silber, ...).
 */
function findPfostenfarbeForRahmen(rahmen: MAuswahlwert): MAuswahlwert | null {
  const mapping = APP.metadata.Farbmapping?.find((m) => m.Auswahlwert?.Key === rahmen.Key);
  const passendePfostenfarbe = mapping?.PassendePfostenfarbe?.[0]?.Auswahlwert;
  if (passendePfostenfarbe) return passendePfostenfarbe;
  return (
    APP.metadata.Auswahlwert.find(
      (a) => a.Auswahl?.Key === "Pfostenfarbe" && a.Name === rahmen.Name
    ) ?? null
  );
}

/**
 * Findet den Schiebetor-Rahmen-Auswahlwert, der zur Pfostenfarbe passt.
 * Primär über die Farbmapping-Tabelle, sekundär über den Namen.
 */
function findRahmenForPfostenfarbe(pfostenfarbe: MAuswahlwert): MAuswahlwert | null {
  const mapping = APP.metadata.Farbmapping?.find((m) =>
    m.PassendePfostenfarbe?.some((p) => p.Auswahlwert?.Key === pfostenfarbe.Key)
  );
  if (mapping?.Auswahlwert?.Auswahl?.Key === "Rahmen") {
    return mapping.Auswahlwert;
  }
  return (
    APP.metadata.Auswahlwert.find(
      (a) =>
        a.Auswahl?.Key === "Rahmen" &&
        /^Schiebetor_Rahmen_/.test(a.Key) &&
        a.Name === pfostenfarbe.Name
    ) ?? null
  );
}

interface PfostenMetaParams {
  meta: MPfosten;
  isUMProfil: boolean;
  hasAnker: boolean;
  pfostenfarbeAuswahlwert: MAuswahlwert;
  anschlagprofilfarbeAuswahlwert: MAuswahlwert;
  bodenbefestigungAuswahlwert: MAuswahlwert;
  elementhalterAuswahlwert: MAuswahlwert;
  pfostenartAuswahlwert: MAuswahlwert;
  isAufgeschraubt: boolean;
  montageadapterAuswahlwert: MAuswahlwert;
  ankerAuswahlwert: MAuswahlwert;
  pfostenkappeAuswahlwert: MAuswahlwert;
  warning?: string;
  box: Box3;
  echteLänge: number;
  dy: number;
  gesuchtePfostenHöhe: number;
  angles: PfostenWinkel[];
}

export class Pfosten extends Jaco {
  static CLASS_NAME = "Pfosten";

  isPfosten = true;

  epsilon = 0.05;

  // ----------------------------------------------------------------------------------
  // persistierte Werte - Zustand, werden mit der Konfiguration gespeichert
  // ----------------------------------------------------------------------------------

  // ist nur gesetzt, wenn abweichend von arrangement.pfostenfarbeAuswahlwert
  // Bei Schiebetorpfosten wird der Wert aus schiebetor.rahmenAuswahlwert abgeleitet
  // bzw. dorthin propagiert, damit Pfostenfarbe und Rahmenfarbe synchron bleiben.
  private _pfostenfarbeAuswahlwert: MAuswahlwert = null;
  pfostenfarbeAuswahlwertIndividualisiert = false;

  get pfostenfarbeAuswahlwert(): MAuswahlwert {
    const rahmen = this.isSchiebetorpfosten ? this.schiebetor?.rahmenAuswahlwert : null;
    if (rahmen) {
      const mapped = findPfostenfarbeForRahmen(rahmen);
      if (mapped) return mapped;
    }
    return this._pfostenfarbeAuswahlwert;
  }

  set pfostenfarbeAuswahlwert(v: MAuswahlwert) {
    this._pfostenfarbeAuswahlwert = v;
    if (v && this.isSchiebetorpfosten) {
      const schiebetor = this.schiebetor;
      if (schiebetor) {
        const matchingRahmen = findRahmenForPfostenfarbe(v);
        if (matchingRahmen && schiebetor.rahmenAuswahlwert !== matchingRahmen) {
          schiebetor.rahmenAuswahlwert = matchingRahmen;
        }
      }
    }
  }
  // ist nur gesetzt, wenn abweichend von arrangement.bodenbefestigungAuswahlwert
  bodenbefestigungAuswahlwert: MAuswahlwert = null;
  bodenbefestigungAuswahlwertIndividualisiert = false;
  // ist nur gesetzt, wenn abweichend von arrangement.pfostenartAuswahlwert
  pfostenartAuswahlwert: MAuswahlwert = null;
  pfostenartAuswahlwertIndividualisiert = false;
  // ist nur gesetzt, wenn abweichend von arrangement.elementhalterAuswahlwert
  elementhalterAuswahlwert: MAuswahlwert = null;
  elementhalterAuswahlwertIndividualisiert = false;

  montageadapterAuswahlwert: MAuswahlwert = null;
  verbundankerAuswahlwert: MAuswahlwert = null;
  ummantelungAuswahlwert: MAuswahlwert = null;
  pfostenkappeAuswahlwert: MAuswahlwert = null;
  ankerAuswahlwert: MAuswahlwert = null;
  ankerInnen = false;
  ausgleichGefälle = false;
  /** ist der Pfosten individualisiert (gelockt?) */
  showIndividualisiert = false;
  anschlagprofilfarbeAuswahlwert: MAuswahlwert = null;

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  meta: MPfosten | MUMProfil = null;
  // nachbarn können auch Mauern oder Empty sein. ACHTUNG: Eckpfosten haben keine Nachbarn!
  nachbarLinks: Part | Aufbau = null;
  nachbarRechts: Part | Aufbau = null;
  // angrenzende Elemente (eins oder zwei), können bei Eckpfosten und komplexerer Form später aber auch mehrere sein
  elemente: ZElement[] = [];
  ecke: Ecke = null;
  hasAnker = false;
  anker: Anker = null;
  montageplatten: ModelGhost[] = null;
  träger: ModelGhost = null;
  bodenplatte: ModelGhost = null;
  ummantelung: ModelGhost = null;
  pfostenkappe: ModelGhost = null;
  isAufgeschraubt = false;
  isUMProfil = false;
  model: ModelGhost = null;
  box = new Box3();
  bestellliste: BestelllistenEintrag[] = [];
  gesamtpreis = 0;
  /** ist sortiert nach angle und anders angeordnet als this.elemente */
  angles: PfostenWinkel[] = [];
  /**
   * matchingRotation ist nur für Tests relevant, die eigentliche Rotation steht im quaternion
   */
  matchingRotation: MatchingRotation = null;
  size = v3();
  anMauer = false;
  // normale unverrückbare Eckpfosten oder die äußeren Pfosten einer Strecke
  isGrenzpfosten = false;
  echteLänge = 0;
  dy = 0;
  massRuler: BoxRuler = null;
  gapLinks = 0;
  gapRechts = 0;
  warning: string = null;
  gesuchtePfostenHöhe = 0;

  hovered = false;

  /** Fundament für einbetonierte Pfosten */
  fundament: Fundament = null;

  // updateCounter = 0;

  constructor(parent) {
    super(parent);
    this.reset();
  }

  get arrangement() {
    return super.arrangement as Arrangement;
  }

  traverse(iterator: (any) => void) {
    super.traverse(iterator);
    this.anker?.traverse(iterator);
    this.montageplatten?.forEach((o) => o.traverse(iterator));
    this.träger?.traverse(iterator);
    this.bodenplatte?.traverse(iterator);
    this.ummantelung?.traverse(iterator);
    this.pfostenkappe?.traverse(iterator);
    this.model?.traverse(iterator);
    this.fundament?.traverse(iterator);
    this.traverseSchiebetorteile(iterator);
  }

  update3d({ mitTorpfostenSonderhöhe }: { mitTorpfostenSonderhöhe?: boolean } = {}) {
    super.update3d();
    const pfostenMetaParameter = getPfostenMetaParameterFromPfosten(this, {
      mitTorpfostenSonderhöhe,
    });
    const {
      meta,
      isUMProfil,
      hasAnker,
      pfostenfarbeAuswahlwert,
      anschlagprofilfarbeAuswahlwert,
      bodenbefestigungAuswahlwert,
      elementhalterAuswahlwert,
      pfostenartAuswahlwert,
      isAufgeschraubt,
      montageadapterAuswahlwert,
      ankerAuswahlwert,
      pfostenkappeAuswahlwert,
      warning,
      box,
      echteLänge,
      dy,
      gesuchtePfostenHöhe,
      angles,
    } = getPfostenMetaParameter(pfostenMetaParameter);
    if (meta == null) {
      debugger;
    }
    this.meta = meta;
    this.isUMProfil = isUMProfil;
    this.hasAnker = hasAnker;
    this.pfostenfarbeAuswahlwert = pfostenfarbeAuswahlwert;
    this.anschlagprofilfarbeAuswahlwert = anschlagprofilfarbeAuswahlwert;
    this.pfostenartAuswahlwert = pfostenartAuswahlwert;
    this.bodenbefestigungAuswahlwert = bodenbefestigungAuswahlwert;
    this.elementhalterAuswahlwert = elementhalterAuswahlwert;
    this.isAufgeschraubt = isAufgeschraubt;
    this.montageadapterAuswahlwert = montageadapterAuswahlwert;
    this.ankerAuswahlwert = ankerAuswahlwert;
    this.pfostenkappeAuswahlwert = pfostenkappeAuswahlwert;
    this.warning = warning;
    this.box = box;
    this.echteLänge = echteLänge;
    this.dy = dy;
    this.gesuchtePfostenHöhe = gesuchtePfostenHöhe;
    this.angles = angles;
    const winkelbereicheParsed = (this.meta as MPfosten).WinkelbereicheParsed;
    this.matchingRotation =
      this.isPfostenAnEcke && winkelbereicheParsed != null
        ? findMatchingRotation(winkelbereicheParsed, this.angles)
        : null;
  }

  update3dModel() {
    // if (this.updateCounter++ > 0) {
    //   console.error(
    //     "segment.update3d sollte immer nur genau ein mal in Kontext von arrangement.update3d aufgerufen werden, weil pfosten.elemente=[] im Arrangement passiert und ansonsten mehrfache Einträge in pfosten.elemente entstehen"
    //   );
    //   debugger;
    // }
    this.resetGhosts();
    if ((this.meta as MUMProfil).Typ == "Montageplatten") {
      this.modelUpdateAsMontageplatten();
    } else if (this.isUMProfil) {
      this.modelUpdateAsUMProfil();
    } else {
      this.modelUpdateAsPfosten();
    }
    this.updateFundament();
  }

  resetGhosts() {
    this.montageplatten = ghostPool(this).clearInstances(this.montageplatten);
    this.model = ghostPool(this).clearInstance(this.model);
    this.träger = ghostPool(this).clearInstance(this.träger);
    this.bodenplatte = ghostPool(this).clearInstance(this.bodenplatte);
    this.anker = ghostPool(this).clearInstance(this.anker?.model);
    this.ummantelung = ghostPool(this).clearInstance(this.ummantelung);
    this.pfostenkappe = ghostPool(this).clearInstance(this.pfostenkappe);
  }

  modelUpdateAsPfosten() {
    // die Pfosten-Modelle können von der StandardHöhe abweichende Längen haben! Länger, was ok ist, oder kürzer, was ein Problem sein kann.
    // Die Höhe über dem Element ist wichtiger als die Tiefe im Boden
    const meta = this.meta as MPfosten;
    const mmr = getMMR(meta);
    this.model = ghostPool(this).getForModelMaterialRelation(this, mmr, {
      size: v3(Number.POSITIVE_INFINITY, this.echteLänge, Number.POSITIVE_INFINITY),
      fixGridPosition: v3(0.5, 1, 0.5),
      gehrung45: false,
    });
    this.model.position.y = this.dy;
    this.box.getSize(this.size);
    this.model.update3d();

    this.additionalModelUpdateAsSchiebetorPfosten();

    if (this.isAufgeschraubt) {
      updateTeilModel(
        this,
        this.isAufgeschraubt,
        "bodenplatte",
        "Bodenplatte-Aufschraub-Pfosten",
        (model) => {
          model.position.copy(model.getBox().grid(0.5, 0, 0.5).negate());
        }
      );
    } else {
      updateTeilModel(
        this,
        !Commons.isEmpty(meta.Träger),
        "träger",
        meta.Träger?.[0]?.Key,
        (model) => {
          model.position.copy(
            model
              .getBox()
              .grid(0.5, 1, 0.5)
              .negate()
              .add(
                this.box
                  .grid(0.5, 0, 0.5)
                  .add(
                    v3(
                      0,
                      this.isTrägerHolzpfosten ? 0.25 : 0,
                      this.isTrägerHolzpfosten && this.isAufgeschraubt ? 0.025 : 0
                    )
                  )
              )
          );
        }
      );
    }

    updateTeilModel(
      this,
      this.hasUmmantelung,
      "ummantelung",
      meta?.Ummantelung?.[0]?.Key,
      (model) => {
        if (model.model.clippingParameters.size.y !== Infinity) {
          model.model.clippingParameters.fixGridPosition.y = 1.0;
        }
        model.position.copy(
          model
            .getBox()
            .grid(0.5, 0, 0.5)
            .negate()
            .add(this.box.grid(0.5, 0, 0.5))
        );
      },
      v3(Number.POSITIVE_INFINITY, this.size.y + 0.001, Number.POSITIVE_INFINITY)
    );

    this.updateAnker();

    updateTeilModel(
      this,
      this.hasPfostenkappe,
      "pfostenkappe",
      this.pfostenkappeAuswahlwert?.Objekt?.[0]?.Key,
      (model) => {
        model.position.copy(
          model
            .getBox()
            .grid(0.5, 0, 0.5)
            .negate()
            .add(this.box.grid(0.5, 0, 0.5))
            .add(v3(0, this.size.y + this.pfostenkappePositionY, 0))
        );
      }
    );
  }

  updateFundament() {
    const meta = this.meta as MPfosten;
    const pfostenmass = meta?.Pfostenmass;
    const fundamentgröße = pfostenmass?.Fundamentgröße;
    const hatFundament = this.isEinbetoniert && !this.isUnechterpfosten && fundamentgröße != null;
    if (hatFundament) {
      if (this.fundament == null) {
        this.fundament = new Fundament(this);
      }
      const hw = fundamentgröße.x / 2;
      const hz = fundamentgröße.z / 2;
      this.fundament.points = [v2(-hw, -hz), v2(hw, -hz), v2(hw, hz), v2(-hw, hz)];
      this.fundament.tiefe = fundamentgröße.y;
      this.fundament.update3d();
    } else {
      this.fundament = null;
    }
  }

  additionalModelUpdateAsSchiebetorPfosten() {
    const meta = this.meta as MPfosten;
    const schlossseite = meta.Torpfostenseite == "Schloss";
    const finalAngle = this.matchingRotation
      ? normalizeAngle(this.matchingRotation.angle + meta.RotationYParsed)
      : 0;

    const schiebetor = this.schiebetor;
    const positionXKorrektur = () => -0.03 * (schiebetor.isAnschlagLinks ? 1 : -1);

    this.model.position.z = 0;
    this.model.quaternion.copy(Maths3.quaternion.zero);
    // finalAngle ist 0, solange der Pfosten keine matchingRotation hat (nur Pfosten an Ecken haben
    // eine). Die Zuweisung muss auch für Schiebetorpfosten passieren, sonst bleibt die Rotation
    // eines vorherigen update3d-Durchlaufs stehen, in dem der Pfosten noch kein Schiebetorpfosten war.
    this.quaternion.setFromAxisAngle(Maths3.vector.y, -finalAngle);
    if (this.isSchiebetorpfosten) {
      this.model.position.z = schiebetor.schiebetorZOffsetPfosten;
      if (schlossseite) {
        this.model.position.z *= 0.65;
      } else {
        this.model.quaternion.copy(
          schiebetor.isAnschlagLinks ? Maths3.quaternion.zero : Maths3.quaternion.y180
        );
      }
    }

    this.model.position.x = this.isSchiebetorDoppelpfosten ? -positionXKorrektur() : 0;

    const updateSchiebetorTeil = ({
      objektKey,
      propertyKey = objektKey,
      condition = true,
      fn,
    }: {
      objektKey: string;
      propertyKey?: string;
      condition?: boolean;
      fn: (model: ModelGhost) => void;
    }) => {
      const objekt = APP.richMetadata.findObjektForTypeAndAuswahlwert(
        objektKey,
        this.pfostenfarbeAuswahlwert?.Key
      );
      updateTeilModel(
        this,
        this.isSchiebetorpfosten && condition,
        propertyKey as any,
        objekt?.Key,
        fn
      );
    };

    updateSchiebetorTeil({
      objektKey: schlossseite
        ? "Schiebetor_Einzelpfostenbodenplatte"
        : "Schiebetor_Doppelpfostenbodenplatte",
      fn: (model) => {
        model.position.z = this.model.position.z - (schlossseite ? 0.1 : 0.16);
        model.position.y = (this.meta as MPfosten)?.Pfostenmass?.PositionY;
        if (!schlossseite) {
          model.position.x = positionXKorrektur();
        }
      },
    });
  }

  Schiebetor_Einzelpfostenbodenplatte: MyModel;
  Schiebetor_Doppelpfostenbodenplatte: MyModel;

  traverseSchiebetorteile(iterator) {
    this.Schiebetor_Einzelpfostenbodenplatte?.traverse(iterator);
    this.Schiebetor_Doppelpfostenbodenplatte?.traverse(iterator);
  }

  updateAnker() {
    if (this.hasAnker) {
      if (this.anker == null) {
        this.anker = new Anker(this);
      }
      this.anker.update3d();
    } else {
      this.anker = null;
    }
  }

  modelUpdateAsUMProfil() {
    const meta = this.meta as MUMProfil;
    const { model, box } = getUMProfilModel(
      this,
      this.gesuchtePfostenHöhe,
      meta,
      (this.nachbarLinks as ZElement)?.isElement
    );
    this.model = model;
    this.box = box;
    this.box.getSize(this.size);
    if (meta.Typ == "Anschlagprofil") {
      const element = this.elemente.find((e) => e.getAnschlagWinkel());
      const anschlagWinkel = element?.getAnschlagWinkel?.();
      if (anschlagWinkel) {
        this.model.position.y = isLongAnschlag(this.meta)
          ? meta.PositionY
          : element.position.y + anschlagWinkel.position.y + meta.PositionY;
      }
    }
  }

  modelUpdateAsMontageplatten() {
    const mmr = getMMR(this.meta);
    const element = this.elemente[0];
    const kloben = (element?.getKloben?.() ?? []).filter((k) => this.isTuerband(k));
    this.montageplatten = kloben.map((kloben) => {
      const montageplatte = ghostPool(this).getForModelMaterialRelation(this, mmr, {
        size: v3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
        fixGridPosition: v3(0, 0, 0),
      });
      const pfostenLinks = element.pfostenLinks === this;
      const zf = -Math.sign(kloben.position.z);
      const zx = pfostenLinks ? 1 : -1;
      const xDiffMauer = pfostenLinks ? 0 : pfostenbreiteCirca / 2;
      const mSize = montageplatte.getSize();
      montageplatte.position.x = 0.006 * zx + 0.02 - xDiffMauer;
      montageplatte.position.y = element.position.y + kloben.position.y;
      montageplatte.position.z = kloben.position.z - mSize.z * (1 - 0.4);
      // montageplatte.position.set(kloben.position.x, kloben.position.y, kloben.position.z);
      montageplatte.scale.set(zx, zx * zf, zf);
      // montageplatte.parent = kloben.parent;
      return montageplatte;
    });
    this.montageplatten.forEach((m) => m.update3d());
    this.box = this.montageplatten.reduce((a, b) => a.union(b.getBox()), new Box3());
    this.box.max.x = this.box.min.x + this.breite;
    this.box.getSize(this.size);
  }

  reset() {
    this.resetGhosts();

    // fixme Defaults geradeziehen mit Element bzw. überdenken
    this.pfostenfarbeAuswahlwert = null;
    this.anschlagprofilfarbeAuswahlwert = null;
    this.pfostenfarbeAuswahlwertIndividualisiert = false;
    this.bodenbefestigungAuswahlwert = null;
    this.bodenbefestigungAuswahlwertIndividualisiert = false;
    this.elementhalterAuswahlwert = null;
    this.elementhalterAuswahlwertIndividualisiert = false;
    this.pfostenartAuswahlwert = null;
    this.pfostenartAuswahlwertIndividualisiert = false;
    this.montageadapterAuswahlwert =
      APP.richMetadata.getAuswahlWertVoreinstellungForKey("Montageadapter");
    this.verbundankerAuswahlwert =
      APP.richMetadata.getAuswahlWertVoreinstellungForKey("Verbundanker");
    this.ummantelungAuswahlwert =
      APP.richMetadata.getAuswahlWertVoreinstellungForKey("Ummantelung");
    this.pfostenkappeAuswahlwert = null;
    this.ankerAuswahlwert = null;
    this.ankerInnen = false;
    this.ausgleichGefälle = false;
    this.träger = null;
    this.bodenplatte = null;

    super.resetCoordinates();

    this.nachbarLinks = null;
    this.nachbarRechts = null;

    this.elemente = [];
    if (this.ecke != null) {
      this.ecke.pfosten = null;
    }
    this.ecke = null;
  }

  get segment() {
    if (this.isPfostenAnEcke) {
      throw new Error("an eckpfosten has no segment");
    }
    const segment = this.parent as Segment;
    return segment?.isSegment ? segment : null;
  }

  getBox() {
    return this.box.clone();
  }

  get selected() {
    return this.arrangement.selectedObjects.includes(this);
  }

  get breite() {
    return getMPfostenBreite(this.meta as MPfosten);
  }

  get snap() {
    if (this.isPfostenAnEcke || this.elemente.length == 0) {
      return 0;
    }
    const elementMitSnapNot0 = this.elemente.find((e) => e.snap != 0);
    if (elementMitSnapNot0) {
      return elementMitSnapNot0.snap;
    }
    if (this.elemente.length == 1) {
      return this.elemente[0].pfostenRechts === this ? -1 : 1;
    }
    return 0;
  }

  set snap(v: number) {
    // nothin..
  }

  get isPfostenAnEcke() {
    return this.ecke != null;
  }

  isPfostenAnEckeUnverrückbar(segment: Segment) {
    return this.elemente.some((e) => e.segment !== segment);
  }

  get isPfostenAnEckeVersetzt() {
    return this.ecke != null && this.ecke.position.distanceTo(this.position) > 0.001;
  }

  get isEckpfostenArtikel() {
    return /\beck/gi.test(this.meta.Key);
  }

  get isPotentiellerEckpfostenArtikel() {
    return this.isPfostenAnEcke && getVorläufigeElementeAnEcke(this.ecke).length > 1;
  }

  // siehe auch Flag isGrenzpfosten

  get isTorpfosten() {
    return ["Torpfosten", "Torpfosten Holz", "Schiebetorpfosten"].includes(
      (this.meta as MPfosten)?.Pfostentyp?.Key
    );
  }

  get isUnechterpfosten() {
    return this.isMontageplatten || this.isAnschlagprofil || this.isUMProfil;
  }

  get isAnschlagprofil() {
    return (this.meta as MUMProfil)?.Typ == "Anschlagprofil";
  }

  get isMontageplatten() {
    return (this.meta as MUMProfil)?.Typ == "Montageplatten";
  }

  get isEinbetoniertMitAnker() {
    return ["Einbetoniert mit Anker_Klemmpfosten", "Einbetoniert mit Anker_WPC_Pfosten"].includes(
      (this.meta as MPfosten)?.Bodenbefestigung?.[0]?.Key
    );
  }

  get isEinbetoniert() {
    return this.bodenbefestigungAuswahlwert?.Key == "Einbetoniert im Boden";
  }

  get isKlemmpfosten() {
    return this.pfostenartAuswahlwert?.Key == "Klemmpfosten";
  }

  get isHolzpfosten() {
    return this.pfostenartAuswahlwert?.Key == "Holzpfosten";
  }

  get hasUmmantelung() {
    return (
      this.ummantelungAuswahlwert.Key == "Ummantelung_mit" &&
      !Commons.isEmpty((this.meta as MPfosten)?.Ummantelung)
    );
  }

  get hasLichtkappe() {
    return /licht/i.test(this.pfostenkappeAuswahlwert?.Key);
  }

  get availableLichtkappen() {
    return (this.meta as MPfosten)?.Pfostenkappe?.filter((k) => k.Key.match(/licht/i)) ?? [];
  }

  get hasPfostenkappe() {
    return (
      this.pfostenkappeAuswahlwert?.Key !== "Pfostenkappe_Ohne" &&
      !Commons.isEmpty(this.pfostenkappeAuswahlwert?.Objekt[0]?.ModelMaterialRelation) &&
      !Commons.isEmpty((this.meta as MPfosten)?.Pfostenkappe)
    );
  }

  get pfostenkappePositionY() {
    return (this.meta as MPfosten).PfostenkappePositionY ?? 0;
  }

  get isTrägerHolzpfosten() {
    return [
      "1885 Träger Holzpfosten zum Aufschrauben",
      "1884 Träger Holzpfosten Erdverbau",
    ].includes((this.meta as MPfosten).Träger?.[0]?.Key);
  }

  get isSchiebetorpfosten() {
    return (this.meta as MPfosten)?.Pfostentyp?.Key === "Schiebetorpfosten";
  }

  get isSchiebetorEinzelpfosten() {
    return (this.meta as MPfosten)?.Pfostenmass?.Key === "Schiebetorpfosten_Einzel";
  }

  get isSchiebetorDoppelpfosten() {
    return (this.meta as MPfosten)?.Pfostenmass?.Key === "Schiebetorpfosten_Doppel";
  }

  get schiebetor() {
    return this.elemente.find((e) => /.*Schiebetor.*/i.test(e.meta.Key));
  }

  getNachbarn() {
    return [this.nachbarLinks, this.nachbarRechts].filter((n) => n != null);
  }

  createJaco3d() {
    return new Pfosten3d(this);
  }

  clone() {
    const clone = new Pfosten(this.parent);
    clone.copy(this);
    return clone;
  }

  copy(other: Pfosten) {
    // super.copy(other);

    this.position.copy(other.position);

    // das hier müssen exakt die persistierten oder session Werte sein!

    this.pfostenfarbeAuswahlwert = other.pfostenfarbeAuswahlwert;
    this.anschlagprofilfarbeAuswahlwert = other.anschlagprofilfarbeAuswahlwert;
    this.pfostenfarbeAuswahlwertIndividualisiert = other.pfostenfarbeAuswahlwertIndividualisiert;
    this.ankerAuswahlwert = other.ankerAuswahlwert;
    this.bodenbefestigungAuswahlwert = other.bodenbefestigungAuswahlwert;
    this.bodenbefestigungAuswahlwertIndividualisiert =
      other.bodenbefestigungAuswahlwertIndividualisiert;
    this.elementhalterAuswahlwert = other.elementhalterAuswahlwert;
    this.elementhalterAuswahlwertIndividualisiert = other.elementhalterAuswahlwertIndividualisiert;
    this.pfostenartAuswahlwert = other.pfostenartAuswahlwert;
    this.pfostenartAuswahlwertIndividualisiert = other.pfostenartAuswahlwertIndividualisiert;
    this.montageadapterAuswahlwert = other.montageadapterAuswahlwert;
    this.verbundankerAuswahlwert = other.verbundankerAuswahlwert;
    this.ummantelungAuswahlwert = other.ummantelungAuswahlwert;
    this.pfostenkappeAuswahlwert = other.pfostenkappeAuswahlwert;
    this.ausgleichGefälle = other.ausgleichGefälle;
    this.ankerInnen = other.ankerInnen;

    // zusätzlich solche, die gecloned werden müssen

    this.meta = other.meta;

    return this;
  }

  toJSON() {
    // super.toJSON() wird nicht benutzt, weil die Info über das Koordinatensystem
    // nicht notwendig ist
    const json: any = {};
    json.type = (this.constructor as any).CLASS_NAME;
    if (this.isPfostenAnEcke) {
      json.position = this.position.toJSON();
    } else {
      json.x0 = Maths.round(this.segment.getX(this, 0), JSON_DECIMALS);
    }
    // das hier müssen exakt die persistierten Werte sein!
    json.pfostenfarbeAuswahlwert = this.pfostenfarbeAuswahlwert?.Key;
    json.anschlagprofilfarbeAuswahlwert = this.anschlagprofilfarbeAuswahlwert?.Key;
    json.pfostenfarbeAuswahlwertIndividualisiert = this.pfostenfarbeAuswahlwertIndividualisiert;

    json.ankerAuswahlwert = this.ankerAuswahlwert?.Key;
    json.bodenbefestigungAuswahlwert = this.bodenbefestigungAuswahlwert?.Key;
    json.bodenbefestigungAuswahlwertIndividualisiert =
      this.bodenbefestigungAuswahlwertIndividualisiert;
    json.elementhalterAuswahlwert = this.elementhalterAuswahlwert?.Key;
    json.elementhalterAuswahlwertIndividualisiert = this.elementhalterAuswahlwertIndividualisiert;
    json.pfostenartAuswahlwert = this.pfostenartAuswahlwert?.Key;

    json.pfostenartAuswahlwertIndividualisiert = this.pfostenartAuswahlwertIndividualisiert;

    json.montageadapterAuswahlwert = this.montageadapterAuswahlwert?.Key;
    json.verbundankerAuswahlwert = this.verbundankerAuswahlwert?.Key;
    json.ummantelungAuswahlwert = this.ummantelungAuswahlwert?.Key;
    json.pfostenkappeAuswahlwert = this.pfostenkappeAuswahlwert?.Key;
    json.ankerInnen = this.ankerInnen;
    json.ausgleichGefälle = this.ausgleichGefälle;
    // // zusätzlich noch ein paar Werte, die eigentlich nicht notwendig sind, aber nützlich sein könnten für weitere Verarbeitung
    json.pfostenAuswahlwert = this.meta?.Auswahlwert?.Key;
    // json.key = this.meta.Key;
    // json.isPfostenAnEcke = this.isPfostenAnEcke;
    // json.dy = Maths.round(this.dy,JSON_DECIMALS);
    // json.echteLänge = Maths.round(this.echteLänge,JSON_DECIMALS);
    // json.breite = Maths.round(this.size.x,JSON_DECIMALS);
    // json.heightPhysical = Maths.round(this.heightPhysical,JSON_DECIMALS);
    // if (!this.isPfostenAnEcke) {
    //     json.x0 = Maths.round(this.segment.getX(this,0),JSON_DECIMALS);
    // }
    return json;
  }

  fromJSON(json: any = {}) {
    // super.fromJSON(json);
    if (this.isPfostenAnEcke) {
      this.position.fromJSON(json.position);
    } else {
      this.segment.setX(this, 0, json.x0);
    }
    // das hier müssen exakt die persistierten Werte sein!
    this.pfostenfarbeAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.pfostenfarbeAuswahlwert,
      "Pfostenfarbe"
    );
    this.anschlagprofilfarbeAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.anschlagprofilfarbeAuswahlwert,
      "Anschlagprofilfarbe"
    );
    this.pfostenfarbeAuswahlwertIndividualisiert =
      json.pfostenfarbeAuswahlwertIndividualisiert ?? false;
    this.ankerAuswahlwert = APP.richMetadata.findAuswahlwert(json.ankerAuswahlwert);
    this.bodenbefestigungAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.bodenbefestigungAuswahlwert,
      "Bodenbefestigung"
    );
    this.bodenbefestigungAuswahlwertIndividualisiert =
      json.bodenbefestigungAuswahlwertIndividualisiert ?? false;
    this.elementhalterAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.elementhalterAuswahlwert,
      "Elementhalter"
    );
    this.elementhalterAuswahlwertIndividualisiert =
      json.elementhalterAuswahlwertIndividualisiert ?? false;
    this.pfostenartAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.pfostenartAuswahlwert,
      "Pfostenart"
    );
    this.pfostenartAuswahlwertIndividualisiert =
      json.pfostenartAuswahlwertIndividualisiert ?? false;
    this.montageadapterAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.montageadapterAuswahlwert,
      "Montageadapter"
    );
    this.verbundankerAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.verbundankerAuswahlwert,
      "Verbundanker"
    );
    this.ummantelungAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.ummantelungAuswahlwert,
      "Ummantelung"
    );
    this.pfostenkappeAuswahlwert = APP.richMetadata.findAuswahlwertOrDefault(
      json.pfostenkappeAuswahlwert,
      "Pfostenkappe"
    );
    this.ankerInnen = json.ankerInnen;
    this.ausgleichGefälle = json.ausgleichGefälle;

    // Abwärtskompatibilität -----------------------
    json.size && this.size.fromJSON(json.size);

    return this;
  }

  /**
   * für Testzwecke: serialisiert zu und nach json, wobei ein Teil der Werte überschrieben werden können
   */
  setJsonPartial(partialJson = {}) {
    this.fromJSON({ ...this.toJSON(), ...partialJson });
  }

  getMeta() {
    return this.meta;
  }

  getBoxUnclipped() {
    return getBoxUnclipped(this.getMeta());
  }

  get hasIndividualisierteAuswahlwerte(): boolean {
    const pfostenAuswahlwerte = [
      "pfostenfarbeAuswahlwertIndividualisiert",
      "bodenbefestigungAuswahlwertIndividualisiert",
      "pfostenartAuswahlwertIndividualisiert",
      "elementhalterAuswahlwertIndividualisiert",
    ];
    return pfostenAuswahlwerte.some((auswahlwert) => this[auswahlwert]);
  }

  get groupTagOwner(): any {
    return this.elemente.find((e) => e.isTor)?.groupTagOwner;
  }

  get ObjectGroupTag(): string {
    return this.groupTagOwner?.ObjectGroupTag ?? this.getJaco3dName() + "_" + this.id;
  }

  get MassOrBauseitsTag(): string {
    return (
      this.groupTagOwner?.MassOrBauseitsTag ??
      (this.gesuchtePfostenHöhe < (this.meta as MPfosten)?.Pfostenmass?.Standardhöhe - this.epsilon
        ? "bauseits"
        : "")
    );
  }

  get isAufschraubpfostenMöglich(): boolean {
    // Übersicht Torbefestigungen: https://www.traumgarten.de/de/torbefestigungen.html
    const isEinzeltorAndDreifachtor = this.elemente.some((e) => e.isEinzeltor && e.isDreifachtor);
    const isMaximalbreiteFürAufschraubpfostenÜberschritten = this.elemente.some(
      (e) => e.isEinzeltor && e.breite > APP.settings.MaximalbreitePfostenAufgeschraubt
    );
    return !isEinzeltorAndDreifachtor && !isMaximalbreiteFürAufschraubpfostenÜberschritten;
  }

  isTuerband(model: ModelGhost) {
    return (
      (model as any).modelMaterialRelation.Model.Key === "Tuerband_Gerade_Mit_Schanier_Schraube"
    );
  }

  collectArtikel(iterator: StücklistenIterator) {
    const ObjectGroupTag = this.ObjectGroupTag;
    iterator({
      ObjectGroupTag,
      JacoId: this.id,
      MassOrBauseitsTag: this.MassOrBauseitsTag,
    });

    const JacoId = this.id;
    iterator({ ObjektMeta: this.meta, ObjectGroupTag, JacoId });
    iterator({ ObjektMeta: _.first((this.meta as MPfosten).Träger), ObjectGroupTag, JacoId });
    this.ankerAuswahlwert && this.anker?.collectArtikel(iterator, ObjectGroupTag);
    this.montageadapterAuswahlwert?.Key == "Montageadapter_mit" &&
      iterator({
        ObjektMeta: _.first((this.meta as MPfosten).Montageadapter),
        ObjectGroupTag,
        JacoId,
      });
    this.verbundankerAuswahlwert?.Key == "Verbundanker-Set_mit" &&
      iterator({
        ObjektMeta: _.first((this.meta as MPfosten).Verbundanker),
        ObjectGroupTag,
        JacoId,
      });
    this.ummantelungAuswahlwert?.Key == "Ummantelung_mit" &&
      iterator({
        ObjektMeta: _.first((this.meta as MPfosten).Ummantelung),
        ObjectGroupTag,
        JacoId,
      });
    const pfostenkappe = findPathIntersectionCached("Pfosten.js:683")(
      this.meta,
      (m: PPfosten) => m.Pfostenkappe,
      this.pfostenkappeAuswahlwert,
      (auswahlwert: PAuswahlwert) => auswahlwert.Objekt
    ) as MObjekt;
    pfostenkappe && iterator({ ObjektMeta: pfostenkappe, ObjectGroupTag, JacoId });
  }

  get heightPhysical(): number {
    return heightBoxWorldPhysical(this.model);
  }

  get logKey(): string {
    return getTypeNameOfPart(this) + "-" + this.id;
  }
}
